<script lang="ts">
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { isWallDisplay, real_id } from '../../../helpers/interface_helpers';
	import { onWifiResponse } from '../NetworkComponents/network_helpers';
	import { capFirstChar } from '../../../helpers/utilities';
	//Services
	import { gen2Request } from '../../../services/gen_2_http_reqs';
	//Stores
		import { getDeviceSettings, getDeviceStatus } from 'src/stores/device-runtime/status-selectors';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	//Design system components
	import { Checkbox, Button, Alert, Icon, Text } from '@Shelly/shelly-design-system';
	//Components
	import NetworkModal from '../NetworkComponents/NetworkModal.svelte';
	import ScanAvailableNetworks from './WiFiScanAvailableNetworksG2.svelte';
	import StaticIPAddressForm from '../NetworkComponents/StaticIPAddressForm.svelte';
	import SsidForm from '../NetworkComponents/SSIDForm.svelte';
	import WifiClientStatus from '../NetworkComponents/WifiClientStatus.svelte';
	//Types
	import type { SettingRequest } from '../../../types/requests/settings/setting';
	import type { Device } from '../../../types/Device';
	import ModalPrompt from 'src/components/ModalPrompt.svelte';
	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';

	$: settingsStore = getDeviceSettings(id);
	export let device: Device;
	export let isOffline;

	const id = real_id(device.id);

	$: statusStore = getDeviceStatus(id);

	let settings = $settingsStore,
		ssid = settings?.wifi.sta1.ssid,
		pass = settings?.wifi.sta1.pass,
		static_ip = settings?.wifi.sta1.ipv4mode === 'static',
		is_open = settings?.wifi.sta1.ssid !== null ? settings?.wifi.sta1.is_open : false,
		enable = settings?.wifi.sta1.enable,
		waitForResponse = false,
		openAlert = false;

	$: staticSettings = {
		ip:
			enable && !static_ip && $statusStore?.wifi?.ssid == ssid
				? $statusStore?.wifi?.sta_ip
				: settings?.wifi.sta1.ip,
		mask: settings?.wifi.sta1.netmask,
		gw: settings?.wifi.sta1.gw,
		dns: settings?.wifi.sta1.nameserver,
	};

	let ssidForm: SsidForm,
		staticIpAddressForm: StaticIPAddressForm,
		modal: NetworkModal,
		postReqData: {
			enable: number;
			ipv4mode?: string;
			ssid?: string;
			pass?: string;
			is_open?: boolean;
			ip?: string;
			netmask?: string;
			gw?: string;
			nameserver?: string;
		};

	const connect = async () => {
		waitForResponse = true;
		const params = {
				config: { sta1: postReqData },
			},
			res: SettingRequest = await gen2Request(device, 'Wifi.SetConfig', params);
		if (res.isok) {
			settings.wifi.sta1 = { ...settings?.wifi.sta1, ...postReqData };
			devices_settings.update_devices_settings(id, settings);
		}
		waitForResponse = false;
		onWifiResponse(res, ssid, enable, device);
	};

	const validate = () => {
		postReqData = { enable };
		if (enable) {
			if (
				ssid !== '' &&
				(ssid != settings?.wifi.sta.ssid || pass != '' || is_open != settings?.wifi.sta.is_open)
			) {
				const ssidParams = ssidForm.validate();
				if (!ssidParams) return false;
				postReqData.ssid = ssidParams.ssid;
				postReqData.is_open = ssidParams.is_open;
				postReqData.pass = ssidParams.pass;
			}
			if (static_ip) {
				const staticIpAddress = staticIpAddressForm.validate();
				if (!staticIpAddress) return false;
				postReqData.ipv4mode = 'static';
				postReqData.ip = staticIpAddress.ip;
				postReqData.netmask = staticIpAddress.mask;
				postReqData.gw = staticIpAddress.gw;
				postReqData.nameserver = staticIpAddress.dns;
			} else {
				postReqData.ipv4mode = 'dhcp';
			}
		}
		if (ssid === '') {
			openAlert = true;
		} else {
			modal.open();
		}
	};

	const clearSSIDData = () => {
		ssid = null;
		enable = false;
		pass = null;
		postReqData = { ssid, pass, enable };
		connect();
	};

	$: hasAnotherNetworkSetUp = settings?.wifi?.sta?.enable;

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			stickyBottomAction.set({ onClick: validate, loading: waitForResponse, disabled: isOffline });
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});
</script>

{#if $showNewLayout}
	<Checkbox size="l" class="mb-xs" bind:checked={enable} disabled={isOffline} id="toggle-501u3df">
		{capFirstChar($format('sub_settings_title_wifi_client_backup', { default: 'wi-fi 2' }))}
	</Checkbox>

	<WifiClientStatus
		{isOffline}
		{...{ id, ssid: $settingsStore?.wifi.sta1.ssid }}
		classes="mb-s-plus"
	/>

	<ScanAvailableNetworks
		{isOffline}
		{device}
		{enable}
		on:select={(e) => {
			ssidForm.reset();
			ssid = e.detail['value'];
			is_open = e.detail['is_open'];
		}}
	/>

	<SsidForm
		{isOffline}
		disableForm={!enable || isOffline}
		bind:this={ssidForm}
		bind:ssid
		bind:pass
		bind:is_open
		removeBottomPadding={isWallDisplay(device.type)}
		removePaddings
	/>

	{#if !isWallDisplay(device.type)}
		<hr class="mt-s mb-m" />
		<StaticIPAddressForm
			removeBottomMargin
			removePaddings
			disabledForm={!enable || isOffline}
			settings={staticSettings}
			bind:this={staticIpAddressForm}
			bind:open={static_ip}
		/>
	{/if}
{:else}
	<Checkbox size="l" class="mb-s" bind:checked={enable} disabled={isOffline} id="checkbox-501u3df">
		{capFirstChar($format('option_enable', { default: 'enable' }))}
	</Checkbox>

	<hr class="mb-m mt-s" />

	<WifiClientStatus {isOffline} {...{ id, ssid: $settingsStore?.wifi.sta1.ssid }} />

	<ScanAvailableNetworks
		{isOffline}
		{device}
		{enable}
		on:select={(e) => {
			ssidForm.reset();
			ssid = e.detail['value'];
			is_open = e.detail['is_open'];
		}}
	/>

	<SsidForm
		{isOffline}
		disableForm={!enable || isOffline}
		bind:this={ssidForm}
		bind:ssid
		bind:pass
		bind:is_open
	/>

	{#if !isWallDisplay(device.type)}
		<StaticIPAddressForm
			disabledForm={!enable || isOffline}
			settings={staticSettings}
			bind:this={staticIpAddressForm}
			bind:open={static_ip}
		/>
	{/if}

	<Button size="m" width="full" on:click={validate} loading={waitForResponse} disabled={isOffline}>
		{$format('button_save', { default: 'Save' })}
	</Button>
{/if}

<NetworkModal
	bind:this={modal}
	on:confirm={connect}
	{isOffline}
	btnTexts={{
		cancelText: '',
		saveText: enable ? $format('button_save') : $format('disable'),
	}}
	title={enable
		? $format('save_wifi_settings_title', {
				default: 'Save Wi-Fi {type} Settings?',
				values: { type: '2' },
			})
		: $format('disable_wifi_settings_title', {
				default: 'Disable Wi-Fi {type}?',
				values: { type: '2' },
			})}
	hideSlotAdditionalInfo={!enable}
>
	{#if enable}
		{$format('wifi_prompt_modal_enable', {
			values: { ssid },
			default: 'Your device will connect to ‘{ssid}’ when the signal is strong enough.',
		})}
	{:else if hasAnotherNetworkSetUp}
		{$format('wifi_prompt_modal_disabled_auto_connect', {
			values: { value: 'Wi-Fi 2', secondValue: 'Wi-Fi 1' },
			default:
				'By disabling ‘{value}’ your device will automatically connect to the configured ‘{secondValue}’ network to maintain connectivity.',
		})}
	{:else}
		{$format('wifi_prompt_modal_disabled', {
			values: { value: 'Wi-Fi 2' },
			default: 'By disabling ‘{value}’ you may lose connection to the device.',
		})}
	{/if}

	<div class="mt-m" slot="additionalInfo">
		<Alert
			type="warning"
			heading={$format('require_device_reboot_prompt', {
				default: 'Note that this action might require a device reboot.',
			})}
		/>
	</div>
</NetworkModal>

{#if openAlert}
	<ModalPrompt
		bind:showModal={openAlert}
		props={{
			callback: clearSSIDData,
			title: `${$format('forget_network_title', {
				default: 'Forget Network',
			})}?`,
			button_1: `${capFirstChar($format('title_forget', { default: 'forget' }))}`,
			button_1_variant: 'danger',
			button_2: $format('button_cancel'),
		}}
	>
		<div class="d-flex flex-direction-column align-items-center">
			<Icon class="mb-xs" icon="text-danger fa-light fa-wifi-slash" size="xxl" />
			<Text class="width-full">
				{$format('wifi_ssd_field_empy_msg', {
					default:
						'The Wi-Fi SSID field is empty. This will forget your saved network. Are you sure you want to proceed?',
				})}
			</Text>
		</div>
	</ModalPrompt>
{/if}
